import React from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { Form as FinalForm } from 'react-final-form';
import { FormattedMessage, injectIntl, intlShape } from '../../../../util/reactIntl';
import * as validators from '../../../../util/validators';
import { pathByRouteName } from '../../../../util/routes';
import routeConfiguration from '../../../../routeConfiguration';
import {
  Form,
  PrimaryButton,
  FieldTextInput,
  FieldPhoneNumberInput,
  FieldCheckbox,
  ExternalLink,
} from '../../../../components';

import { isAgeConfirmed } from '../../../../util/signupProtectedData';

import css from '../SignupWizard.module.css';

export const ProfileStepComponent = props => {
  const { formData, onSubmit, inProgress, intl, onOpenTermsOfService, role } = props;

  const isBetaMode = process.env.REACT_APP_BETA_MODE === 'true';
  const isCreator = role === 'creator';
  const isOrganization = role === 'organization';

  // In community-only mode, hide phone and organization name. The required terms box there
  // already reads "I am 18 or older and I agree to...", so it confirms the age instead of a
  // separate "I'm 18 or older" box (no date of birth either way, Privacy B.1).
  const showPhoneNumber = !isBetaMode && (isCreator || isOrganization);
  const showOrganizationName = !isBetaMode && isOrganization;

  return (
    <FinalForm
      initialValues={{
        firstName: formData.firstName,
        lastName: formData.lastName,
        organizationName: formData.organizationName,
        ageConfirmed: formData.ageConfirmed || [],
        phoneNumber: formData.phoneNumber,
        // Beta-mode consent checkboxes — both default off so the
        // required one starts in a "needs action" state.
        termsAccepted: formData.termsAccepted || false,
        newsletterOptIn: formData.newsletterOptIn || false,
      }}
      onSubmit={values =>
        onSubmit(isBetaMode ? { ...values, ageConfirmed: values.termsAccepted } : values)
      }
      render={fieldRenderProps => {
        const { handleSubmit, invalid, submitting, values } = fieldRenderProps;

        // First name
        const firstNameLabel = intl.formatMessage({ id: 'SignupWizard.firstNameLabel' });
        const firstNamePlaceholder = intl.formatMessage({
          id: 'SignupWizard.firstNamePlaceholder',
        });
        const firstNameRequiredMessage = intl.formatMessage({ id: 'SignupForm.firstNameRequired' });
        const firstNameRequired = validators.required(firstNameRequiredMessage);

        // Last name
        const lastNameLabel = intl.formatMessage({ id: 'SignupWizard.lastNameLabel' });
        const lastNamePlaceholder = intl.formatMessage({ id: 'SignupWizard.lastNamePlaceholder' });
        const lastNameRequiredMessage = intl.formatMessage({ id: 'SignupForm.lastNameRequired' });
        const lastNameRequired = validators.required(lastNameRequiredMessage);

        // Organization name (for organizations)
        const organizationNameLabel = intl.formatMessage({
          id: 'SignupWizard.organizationNameLabel',
        });
        const organizationNamePlaceholder = intl.formatMessage({
          id: 'SignupWizard.organizationNamePlaceholder',
        });
        const organizationNameRequiredMessage = intl.formatMessage({
          id: 'SignupWizard.organizationNameRequired',
        });
        const organizationNameRequired = validators.required(organizationNameRequiredMessage);

        // Phone number (for creators and organizations)
        const phoneNumberLabel = intl.formatMessage({ id: 'SignupWizard.phoneNumberLabel' });
        const phoneNumberPlaceholder = intl.formatMessage({
          id: 'SignupWizard.phoneNumberPlaceholder',
        });

        const submitDisabled = invalid || submitting || inProgress;

        // Terms link for accessibility
        const handleTermsKeyUp = e => {
          if (e.keyCode === 13) {
            onOpenTermsOfService();
          }
        };

        const termsLink = (
          <span
            className={css.termsLink}
            onClick={onOpenTermsOfService}
            role="button"
            tabIndex="0"
            onKeyUp={handleTermsKeyUp}
          >
            <FormattedMessage id="SignupForm.termsAndConditionsLinkText" />
          </span>
        );

        return (
          <Form className={css.stepForm} onSubmit={handleSubmit}>
            <FieldTextInput
              className={css.field}
              type="text"
              id="firstName"
              name="firstName"
              autoComplete="given-name"
              label={firstNameLabel}
              placeholder={firstNamePlaceholder}
              validate={firstNameRequired}
            />

            <FieldTextInput
              className={css.field}
              type="text"
              id="lastName"
              name="lastName"
              autoComplete="family-name"
              label={lastNameLabel}
              placeholder={lastNamePlaceholder}
              validate={lastNameRequired}
            />

            {showOrganizationName && (
              <FieldTextInput
                className={css.field}
                type="text"
                id="organizationName"
                name="organizationName"
                autoComplete="organization"
                label={organizationNameLabel}
                placeholder={organizationNamePlaceholder}
                validate={organizationNameRequired}
              />
            )}

            {showPhoneNumber && (
              <FieldPhoneNumberInput
                className={css.field}
                id="phoneNumber"
                name="phoneNumber"
                label={phoneNumberLabel}
                placeholder={phoneNumberPlaceholder}
              />
            )}

            {!isBetaMode && (
              <FieldCheckbox
                className={css.field}
                id="ageConfirmed"
                name="ageConfirmed"
                value="confirmed"
                label={<FormattedMessage id="SignupForm.ageConfirm" />}
              />
            )}

            <div className={css.bottomWrapper}>
              {isBetaMode ? (
                <div className={css.consentCheckboxes}>
                  <FieldCheckbox
                    id="termsAccepted"
                    name="termsAccepted"
                    value="accepted"
                    label={
                      <FormattedMessage
                        id="SignupForm.consentTerms"
                        values={{
                          privacyPolicyLink: (
                            <ExternalLink
                              href={pathByRouteName('PrivacyPolicyPage', routeConfiguration())}
                              className={css.consentLink}
                            >
                              <FormattedMessage id="SignupForm.consentPrivacyPolicyLink" />
                            </ExternalLink>
                          ),
                          termsLink: (
                            <ExternalLink
                              href={pathByRouteName('TermsOfServicePage', routeConfiguration())}
                              className={css.consentLink}
                            >
                              <FormattedMessage id="SignupForm.consentTermsLink" />
                            </ExternalLink>
                          ),
                        }}
                      />
                    }
                  />
                  <FieldCheckbox
                    id="newsletterOptIn"
                    name="newsletterOptIn"
                    value="optedIn"
                    label={<FormattedMessage id="SignupForm.consentNewsletter" />}
                  />
                </div>
              ) : (
                <p className={css.termsText}>
                  <FormattedMessage
                    id="SignupForm.termsAndConditionsAcceptText"
                    values={{ termsLink }}
                  />
                </p>
              )}
              {/* FieldCheckbox renders react-final-form's
                  <Field type="checkbox" value="accepted">, which is
                  multi-checkbox mode — values.termsAccepted is an
                  array, not a boolean. After tick-then-untick the
                  array becomes [] which is truthy, so a naive
                  `!values.termsAccepted` falsely keeps the button
                  enabled. Check the array length explicitly. */}
              <PrimaryButton
                type="submit"
                inProgress={inProgress}
                disabled={
                  submitDisabled ||
                  !isAgeConfirmed(isBetaMode ? values.termsAccepted : values.ageConfirmed)
                }
              >
                <FormattedMessage id="SignupWizard.signUpButton" />
              </PrimaryButton>
            </div>
          </Form>
        );
      }}
    />
  );
};

ProfileStepComponent.defaultProps = {
  inProgress: false,
  role: 'audience',
};

ProfileStepComponent.propTypes = {
  formData: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
  inProgress: PropTypes.bool,
  intl: intlShape.isRequired,
  onOpenTermsOfService: PropTypes.func.isRequired,
  role: PropTypes.oneOf(['audience', 'creator', 'organization']),
};

const ProfileStep = compose(injectIntl)(ProfileStepComponent);

export default ProfileStep;
